import React, { useState, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MainCard from '@/components/MainCard';
import { Row, Col, Table, Tag, Space, Input, Select, Modal, Button, Card } from 'antd';
import StatCard from '@/components/StatCard';
import { FileCheck, Clock, CheckCircle2, XCircle, Check, X, Filter } from 'lucide-react';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import { defaultColors } from '@/themes/antd-theme';

const { Search } = Input;
const { TextArea } = Input;

// Predefined request types with different status colors
const REQUEST_TYPES = [
  { type: 'Finance', color: 'primary' },
  { type: 'HR', color: 'secondary' },
  { type: 'IT', color: 'info' },
  { type: 'Operations', color: 'warning' },
  { type: 'Procurement', color: 'success' },
];

const STATUS_COLORS = {
  Pending: 'warning',
  Approved: 'success',
  Rejected: 'error',
  InProgress: 'info',
};

// Initial mock data with more detailed batch processing requests
const initialRequests = [
  {
    id: "BP101",
    name: "Equipment Batch Upgrade",
    type: "Operations",
    status: "Pending",
    description: "Request to upgrade all assembly line equipment in Batch A",
    requestedBy: "Alice Brown",
    date: "2024-04-01"
  },
  {
    id: "BP102",
    name: "New Machine Approval - Batch Line 3",
    type: "Procurement",
    status: "Pending",
    description: "Approval for purchasing a new CNC machine for Batch Line 3",
    requestedBy: "Bob Green",
    date: "2024-04-05"
  },
  {
    id: "BP103",
    name: "Batch Process Redesign - Chemical Mixing",
    type: "Finance",
    status: "Pending",
    description: "Budget approval for redesigning the chemical mixing process in Batch Unit 7",
    requestedBy: "Charlie White",
    date: "2024-04-10"
  },
  {
    id: "BP104",
    name: "Safety Protocol Review - Batch Processing",
    type: "HR",
    status: "Pending",
    description: "Review and approve updated safety protocols for all batch processing units",
    requestedBy: "Diana Black",
    date: "2024-04-12"
  },
  {
    id: "BP105",
    name: "IT Infrastructure Upgrade - Batch System",
    type: "IT",
    status: "Pending",
    description: "Request for upgrading IT infrastructure supporting batch processing systems",
    requestedBy: "Eve Grey",
    date: "2024-04-15"
  },
  {
    id: "BP106",
    name: "Raw Material Procurement - Batch Cycle 5",
    type: "Procurement",
    status: "Pending",
    description: "Approval to procure raw materials for Batch Cycle 5 production run",
    requestedBy: "Frank Blue",
    date: "2024-04-18"
  },
  {
    id: "BP107",
    name: "Quality Control System - Batch Output Analysis",
    type: "Operations",
    status: "Pending",
    description: "Implement new quality control system for analyzing batch output data",
    requestedBy: "Grace Red",
    date: "2024-04-20"
  },
  {
    id: "BP108",
    name: "Training Program - New Batch Machine Operators",
    type: "HR",
    status: "Pending",
    description: "Approval for a training program for new operators of batch processing machines",
    requestedBy: "Henry Yellow",
    date: "2024-04-25"
  }
];

const ApproveRequest = () => {
  const [requests, setRequests] = useState(initialRequests);
  const [approverName, setApproverName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [filterType, setFilterType] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [approverDescription, setApproverDescription] = useState("");

  // Get Tag color based on type
  const getTypeColor = (type) => {
    const typeColors = {
      'Finance': 'blue',
      'HR': 'purple',
      'IT': 'cyan',
      'Operations': 'orange',
      'Procurement': 'green',
    };
    return typeColors[type] || 'default';
  };

  // Get Tag color based on status
  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': 'warning',
      'Approved': 'success',
      'Rejected': 'error',
      'InProgress': 'processing',
    };
    return statusColors[status] || 'default';
  };

  // Define table columns
  const allColumns = [
    {
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      defaultVisible: true,
      sortable: true,
      sortType: "string",
    },
    {
      title: "Request Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      defaultVisible: true,
      sortable: true,
      sortType: "string",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      defaultVisible: true,
      sortable: true,
      sortType: "string",
      render: (type) => <Tag color={getTypeColor(type)}>{type}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      defaultVisible: true,
      sortable: false,
      sortType: "string",
      ellipsis: true,
    },
    {
      title: "Requested By",
      dataIndex: "requestedBy",
      key: "requestedBy",
      width: 150,
      defaultVisible: true,
      sortable: true,
      sortType: "string",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      defaultVisible: true,
      sortable: true,
      sortType: "string",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      defaultVisible: true,
      sortable: true,
      sortType: "string",
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: 'right',
      defaultVisible: true,
      sortable: false,
      sortType: "string",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<Check size={20} />}
          onClick={() => handleApproveRequest(record.id)}
          style={{
            backgroundColor: defaultColors.primary,
            borderColor: defaultColors.primary,
          }}
        >
          Action
        </Button>
      ),
    },
  ];

  // Advanced filtering and search
  const filteredRequests = useMemo(() => {
    return requests.filter(request =>
      (filterType === "All" || request.type === filterType) &&
      (request.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [requests, filterType, searchTerm]);

  // Use table config hook
  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: "approveRequestTable",
    columns: allColumns,
    data: filteredRequests,
    searchValue: searchTerm,
  });

  // Handle Approve Request for a single row
  const handleApproveRequest = (id) => {
    setCurrentRequestId(id);
    setApproverName("");
    setNameError(false);
    setOpenConfirmDialog(true);
    setApproverDescription("");
  };

  // Confirm Approval for a single request
  const confirmApproval = () => {
    setNameError(false);

    if (!approverName.trim()) {
      setNameError(true);
      return;
    }

    if (!currentRequestId) {
      console.error("No request ID to approve.");
      return;
    }

    const requestNameToDisplay = requests.find(req => req.id === currentRequestId)?.name || 'Request';

    setRequests(prevRequests =>
      prevRequests.map(req =>
        req.id === currentRequestId
          ? { ...req, status: "Approved", approvalDescription: approverDescription }
          : req
      )
    );
    setApproverName("");
    setOpenConfirmDialog(false);
    toast.success(`${requestNameToDisplay} approved successfully!`);
    setCurrentRequestId(null);
    setApproverDescription("");
  };

  return (
    <MainCard>
      {/* Top Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <StatCard
            label="Total Requests"
            value={requests?.length || 0}
            icon={<FileCheck size={24} />}
            bgColor="#dbeafe"
            iconColor="#3b82f6"
          />
        </Col>
        <Col span={6}>
          <StatCard
            label="Pending"
            value={requests?.filter(r => r?.status === "Pending")?.length || 0}
            icon={<Clock size={24} />}
            bgColor="#fef9c3"
            iconColor="#facc15"
          />
        </Col>
        <Col span={6}>
          <StatCard
            label="Approved"
            value={requests?.filter(r => r?.status === "Approved")?.length || 0}
            icon={<CheckCircle2 size={24} />}
            bgColor="#d1fae5"
            iconColor="#10b981"
          />
        </Col>
        <Col span={6}>
          <StatCard
            label="Rejected"
            value={requests?.filter(r => r?.status === "Rejected")?.length || 0}
            icon={<XCircle size={24} />}
            bgColor="#fee2e2"
            iconColor="#ef4444"
          />
        </Col>
      </Row>

      <Card style={{ width: '100%' }}>
        <div className="p-6">
          <h4 className="text-xl font-bold text-center mb-6" style={{ color: defaultColors.primary }}>
            Batch Approval Dashboard
          </h4>

          {/* Filters and Search */}
          <div className="flex justify-between items-center mb-4">
            <Space size="middle">
              <Select
                value={filterType}
                onChange={(value) => setFilterType(value)}
                style={{ width: 200 }}
                suffixIcon={<Filter size={16} />}
              >
                <Select.Option value="All">All Types</Select.Option>
                {REQUEST_TYPES.map(type => (
                  <Select.Option key={type.type} value={type.type}>
                    {type.type}
                  </Select.Option>
                ))}
              </Select>

              <Search
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={(value) => setSearchTerm(value)}
                style={{ width: 300 }}
                allowClear
              />

              <TableColumnConfig columns={allColumns} tableKey="approveRequestTable" />
            </Space>
          </div>

          {/* Request Table */}
          <Table
            dataSource={filteredData}
            columns={visibleColumns}
            size="small"
            bordered
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} requests`,
              showQuickJumper: true,
            }}
            scroll={{ x: 1200 }}
          />

          {/* Confirmation Modal */}
          <Modal
            title="Approve Request"
            open={openConfirmDialog}
            onCancel={() => setOpenConfirmDialog(false)}
            footer={[
              <Button
                key="cancel"
                icon={<X size={16} />}
                onClick={() => setOpenConfirmDialog(false)}
              >
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                icon={<Check size={16} />}
                onClick={confirmApproval}
                disabled={nameError || !approverName.trim()}
                style={{
                  backgroundColor: defaultColors.primary,
                  borderColor: defaultColors.primary,
                }}
              >
                Confirm Approve
              </Button>
            ]}
          >
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                Please enter your name to approve this request.
              </p>

              <div>
                <label className="block text-sm font-medium mb-2">Approver Name</label>
                <Input
                  placeholder="Enter your name"
                  value={approverName}
                  onChange={(e) => setApproverName(e.target.value)}
                  status={nameError ? 'error' : ''}
                />
                {nameError && (
                  <p className="text-sm mt-1" style={{ color: defaultColors.error }}>
                    Please enter your name to approve the request
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Approval Description (Optional)</label>
                <TextArea
                  placeholder="Enter approval description"
                  rows={3}
                  value={approverDescription}
                  onChange={(e) => setApproverDescription(e.target.value)}
                />
              </div>

              <div className="mt-4 space-y-2">
                <p className="text-gray-600">
                  <strong>Request Description:</strong> {requests.find(req => req.id === currentRequestId)?.description}
                </p>
                <p className="text-gray-600">
                  Are you sure you want to approve request ID: <strong>{currentRequestId}</strong>?
                </p>
              </div>
            </div>
          </Modal>
        </div>
      </Card>
      <ToastContainer />
  
    </MainCard>
  );
};

export default ApproveRequest;